import { useDocContentGeneration } from './useDocContentGeneration'
import { useCallback } from 'react'
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import {
  TVendorAdminDocData,
  TCommunityInvoicesAdminDocData,
  TCommunityRemitAdvicesAdminDocData,
  THSRemitAdvicesAdminDocData,
  TCommonsAdminDocData,
  TGPOInvoicesAdminDocData,
  TGPORemmitanceAdminDocData
} from './types'
import {
  COMMENTS_ON_INSTRUCTIONS,
  DEFAULT_PDF_DOC_TXT,
  PDF_TYPES
} from './constants'

export const useFinancialsAdminDocGeneration = () => {
  const {
    getPdfFileName,
    generateDocumentInfo,
    generateFromToSection,
    generateCommentsAndTotalSection,
    launchDocGeneration,
    generateCommunityRemittanceAdviceTable,
    generateVendorInvoicesTable,
    generateSummaryTable,
    generateCommunityInvoicesTable,
    generateGPOInvoicesTable,
    generateHSRemittanceAdviceTable,
    generateCommonsAdminRemittanceAdviceTable,
    generateGPORemittanceTable
  } = useDocContentGeneration()

  const generateCommunityAdminDocHeader = useCallback(
    (
      communityData:
        | TCommunityInvoicesAdminDocData['communityData']
        | TCommunityRemitAdvicesAdminDocData['communityData'],
      invoiceDoc: boolean
    ): Content[] => {
      return [
        ...generateDocumentInfo({
          docTitle: invoiceDoc
            ? DEFAULT_PDF_DOC_TXT.INVOICE
            : DEFAULT_PDF_DOC_TXT.REMITTANCE_ADVICE,
          docHeaderName: invoiceDoc
            ? DEFAULT_PDF_DOC_TXT.INVOICE
            : DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          docDate: communityData.remit_advice_date,
          docId: communityData.document_number,
          docDueOn: communityData.due_date
        }),
        ...generateFromToSection(
          {
            name: communityData.name_from,
            address: communityData.address,
            id: communityData.department_number
          },
          {
            name: communityData.bill_to_name,
            address: communityData.bill_to_address,
            phoneNumber: communityData.bill_to_phone_number
          }
        )
      ]
    },
    [generateDocumentInfo, generateFromToSection]
  )

  const generateCommonsAdminDocHeader = useCallback(
    (commonsData: TCommonsAdminDocData['commonsData']): Content[] => {
      return [
        ...generateDocumentInfo({
          docTitle: DEFAULT_PDF_DOC_TXT.REMITTANCE_ADVICE,
          docHeaderName: DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          docDate: commonsData.remit_advice_date,
          docId: commonsData.document_number,
          docDueOn: commonsData.due_date
        }),
        ...generateFromToSection(
          {
            name: commonsData.name_from,
            address: commonsData.address
          },
          {
            name: commonsData.bill_to_name,
            address: commonsData.bill_to_address,
            phoneNumber: commonsData.bill_to_phone_number
          }
        )
      ]
    },
    [generateDocumentInfo, generateFromToSection]
  )

  const generateVendorAdminDocHeader = useCallback(
    (
      vendorData: TVendorAdminDocData['vendorData'],
      invoiceDoc: boolean
    ): Content[] => {
      return [
        ...generateDocumentInfo({
          docTitle: invoiceDoc
            ? DEFAULT_PDF_DOC_TXT.INVOICE
            : DEFAULT_PDF_DOC_TXT.REMITTANCE_ADVICE,
          docHeaderName: invoiceDoc
            ? DEFAULT_PDF_DOC_TXT.INVOICE
            : DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          docDate: vendorData.remit_advice_date,
          docId: vendorData.document_number,
          docDueOn: vendorData.due_date
        }),
        ...generateFromToSection(
          {
            name: vendorData.name_from,
            address: vendorData.address,
            id: vendorData.department_number
          },
          {
            name: vendorData.bill_to_name,
            address: vendorData.bill_to_address,
            phoneNumber: vendorData.bill_to_phone_number
          }
        )
      ]
    },
    [generateDocumentInfo, generateFromToSection]
  )

  const generateCommunityRemitAdvicesAdminDoc = useCallback(
    ({ communityData, callback }: TCommunityRemitAdvicesAdminDocData) => {
      const fromCOToGPOLedByGPO =
        communityData.pdf_type === PDF_TYPES.COMMUNITY_LED_BY_GPO

      const detailedTable = fromCOToGPOLedByGPO
        ? generateSummaryTable(communityData.detail_table)
        : []
      const content: TDocumentDefinitions['content'] = [
        ...generateCommunityAdminDocHeader(communityData, false),
        ...generateCommunityRemittanceAdviceTable(
          communityData.invoices,
          fromCOToGPOLedByGPO
        ),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.CREDIT_NET],
          totalDue: fromCOToGPOLedByGPO
            ? {
                label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
                value: communityData.total_due ?? ''
              }
            : undefined,
          data: fromCOToGPOLedByGPO
            ? [
                {
                  label: DEFAULT_PDF_DOC_TXT.SUBTOTAL_PROCESSING_FEE,
                  value: communityData.subtotal_processing_fee ?? ''
                },
                {
                  label: DEFAULT_PDF_DOC_TXT.NET_TO_COMMUNITY,
                  value: communityData.net_to
                }
              ]
            : [
                {
                  label: DEFAULT_PDF_DOC_TXT.NET_TO_COMMUNITY,
                  value: communityData.net_to
                }
              ]
        }),
        ...detailedTable
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          communityData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateCommunityAdminDocHeader,
      generateCommunityRemittanceAdviceTable,
      generateCommentsAndTotalSection,
      launchDocGeneration,
      generateSummaryTable
    ]
  )

  const generateHSRemitAdvicesAdminDoc = useCallback(
    ({ communityData, callback }: THSRemitAdvicesAdminDocData) => {
      const fromHSToGPO = communityData.pdf_type === PDF_TYPES.HS_LED_BY_GPO

      const content: TDocumentDefinitions['content'] = [
        ...generateCommunityAdminDocHeader(communityData, false),
        ...generateHSRemittanceAdviceTable(communityData.invoices, fromHSToGPO),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.CREDIT_NET],
          totalDue: {
            label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
            value: communityData.total_due ?? ''
          },
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.SUBTOTAL_PROCESSING_FEE,
              value: communityData.subtotal_processing_fee ?? ''
            },
            {
              label: DEFAULT_PDF_DOC_TXT.NET_TO_HEALTH_SYSTEM,
              value: communityData.net_to
            }
          ]
        }),
        ...generateSummaryTable(communityData.detail_table)
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          communityData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateCommunityAdminDocHeader,
      generateCommentsAndTotalSection,
      launchDocGeneration,
      generateSummaryTable,
      generateHSRemittanceAdviceTable
    ]
  )

  const generateCommunityInvoicesAdminDoc = useCallback(
    ({ communityData, callback }: TCommunityInvoicesAdminDocData) => {
      const isFromGPO =
        communityData.pdf_type === PDF_TYPES.COMMUNITY_INVOICE_FROM_GPO
      const content: TDocumentDefinitions['content'] = [
        ...generateCommunityAdminDocHeader(communityData, true),
        ...generateCommunityInvoicesTable(communityData.invoices, isFromGPO),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.DEBIT],
          totalDue: {
            label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
            value: communityData.total_due ?? ''
          },
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.SUBTOTAL_PROCESSING_FEE,
              value: communityData.subtotal_processing_fee ?? ''
            },
            {
              label: isFromGPO
                ? DEFAULT_PDF_DOC_TXT.NET_TO_GPO
                : DEFAULT_PDF_DOC_TXT.NET_TO_HEALTH_SYSTEM,
              value: communityData.net_to
            }
          ]
        }),
        ...generateSummaryTable(communityData.detail_table)
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.INVOICE_DOC_NAME,
          communityData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateCommunityAdminDocHeader,
      generateCommentsAndTotalSection,
      launchDocGeneration,
      generateSummaryTable,
      generateCommunityInvoicesTable
    ]
  )

  const generateCommonsAdminDoc = useCallback(
    ({ commonsData, callback }: TCommonsAdminDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ...generateCommonsAdminDocHeader(commonsData),
        ...generateCommonsAdminRemittanceAdviceTable(commonsData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [
            COMMENTS_ON_INSTRUCTIONS.PROCESSING_FEE,
            COMMENTS_ON_INSTRUCTIONS.CREDIT
          ],
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.TOTAL_AF_SPLIT,
              value: commonsData.total_admin_fee_split
            },
            {
              label: DEFAULT_PDF_DOC_TXT.OM_COMMUNITY_FEE,
              value: commonsData.cognus_processing_fee
            }
          ]
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          commonsData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateCommonsAdminDocHeader,
      generateCommonsAdminRemittanceAdviceTable,
      generateCommentsAndTotalSection,
      launchDocGeneration
    ]
  )

  const generateVendorInvoicesAdminDoc = useCallback(
    ({ vendorData, callback }: TVendorAdminDocData) => {
      const content: TDocumentDefinitions['content'] = [
        ...generateVendorAdminDocHeader(vendorData, true),
        ...generateVendorInvoicesTable(vendorData.invoices),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.DEBIT],
          totalDue: {
            label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
            value: vendorData.total_due ?? ''
          }
        })
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.INVOICE_DOC_NAME,
          vendorData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateVendorAdminDocHeader,
      generateCommentsAndTotalSection,
      launchDocGeneration,
      generateVendorInvoicesTable
    ]
  )

  const generateGPOInvoicesAdminDoc = useCallback(
    ({ gpoData, callback }: TGPOInvoicesAdminDocData) => {
      const isFromHS = gpoData.pdf_type === PDF_TYPES.GPO_INVOICE_FROM_HS
      const content: TDocumentDefinitions['content'] = [
        ...generateVendorAdminDocHeader(gpoData, true),
        ...generateGPOInvoicesTable(gpoData.invoices, isFromHS),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.DEBIT],
          totalDue: {
            label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
            value: gpoData.total_due ?? ''
          },
          data: [
            {
              label: DEFAULT_PDF_DOC_TXT.SUBTOTAL_PROCESSING_FEE,
              value: gpoData.subtotal_processing_fee ?? ''
            },
            {
              label: isFromHS
                ? DEFAULT_PDF_DOC_TXT.NET_TO_HEALTH_SYSTEM
                : DEFAULT_PDF_DOC_TXT.NET_TO_COMMUNITY,
              value: gpoData.net_to
            }
          ]
        }),
        ...generateSummaryTable(gpoData.detail_table)
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.INVOICE_DOC_NAME,
          gpoData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateVendorAdminDocHeader,
      generateCommentsAndTotalSection,
      generateSummaryTable,
      launchDocGeneration,
      generateGPOInvoicesTable
    ]
  )

  const generateGPORemittanceAdviceAdminDoc = useCallback(
    ({ gpoData, callback }: TGPORemmitanceAdminDocData) => {
      const isFromGPOLedByGPO = gpoData.pdf_type === PDF_TYPES.GPO_LED_BY_GPO

      const detailedTable = isFromGPOLedByGPO
        ? []
        : generateSummaryTable(gpoData.detail_table)
      const content: TDocumentDefinitions['content'] = [
        ...generateVendorAdminDocHeader(gpoData, false),
        ...generateGPORemittanceTable(gpoData.invoices, isFromGPOLedByGPO),
        ...generateCommentsAndTotalSection({
          comments: [COMMENTS_ON_INSTRUCTIONS.DEBIT],
          totalDue: isFromGPOLedByGPO
            ? undefined
            : {
                label: DEFAULT_PDF_DOC_TXT.TOTAL_DUE,
                value: gpoData.total_due ?? ''
              },
          data: isFromGPOLedByGPO
            ? [
                {
                  label: DEFAULT_PDF_DOC_TXT.NET_TO_GPO,
                  value: gpoData.net_to
                }
              ]
            : [
                {
                  label: DEFAULT_PDF_DOC_TXT.SUBTOTAL_PROCESSING_FEE,
                  value: gpoData.subtotal_processing_fee ?? ''
                },
                {
                  label: DEFAULT_PDF_DOC_TXT.NET_TO_GPO,
                  value: gpoData.net_to
                }
              ]
        }),
        ...detailedTable
      ]
      launchDocGeneration(
        content,
        getPdfFileName(
          DEFAULT_PDF_DOC_TXT.REMIT_ADVICE,
          gpoData.document_number
        ),
        callback
      )
    },
    [
      getPdfFileName,
      generateVendorAdminDocHeader,
      generateCommentsAndTotalSection,
      generateSummaryTable,
      launchDocGeneration,
      generateGPORemittanceTable
    ]
  )

  return {
    generateCommunityRemitAdvicesAdminDoc,
    generateCommonsAdminDoc,
    generateCommunityInvoicesAdminDoc,
    generateGPOInvoicesAdminDoc,
    generateVendorInvoicesAdminDoc,
    generateHSRemitAdvicesAdminDoc,
    generateGPORemittanceAdviceAdminDoc
  }
}
